import { useForm } from "react-hook-form";
import { DropDownGeneric } from "../../../components/dropdowns";
import { ErrorText } from "../../../components/forms/ErrorText";
import { API, buildUrl, FORM_MSG } from "../../../config";
import ExpDate from "../../../components/inputs/ExpDate";
import { InputRFH } from "../../../components/inputs/InputRFH";
import { useEffect, useMemo } from "react";
import { useRouteMatch } from "react-router-dom";
import { useUpdate, useHandleError, useDelete } from "../../../hooks";
import {
    NOTIFY_CLIENT_PAYMENT_METHOD_CREATED,
    NOTIFY_CLIENT_PAYMENT_METHOD_DELETED,
    NOTIFY_CLIENT_PAYMENT_METHOD_UPDATED,
    openNotification,
} from "../../../utils";
import { PaymentMethod, PaymentMethodType } from "../../../types";
import CCInput from "../../../components/inputs/CCInput";
import CVCInput from "../../../components/inputs/CVCInput";
import { getCardTypeByValue } from "../../../utils/cardFormatter";
import Loader from "react-loader-spinner";

type PaymentFormValues = {
    id: string;
    paymentMethod: string;
    cardHolderName?: string;
    creditCardNumber?: string;
    expDate?: string;
    ccv?: string;
    bankName?: string;
    accountNumber?: string;
    routingNumber?: string;
    xpSystem?: string;
};

const paymentMethodOptions = ["Credit Card", "Bank Account", "XP information"];

interface AddPaymentMethodProps {
    onCancel: () => void;
    mutate: Function;
    selectedPaymentMethod?: PaymentMethodType;
    selectedPaymentMethodData?: PaymentMethod;
    isLead?: boolean;
}

export const AddPaymentMethod = ({
    onCancel,
    mutate,
    selectedPaymentMethod,
    selectedPaymentMethodData,
    isLead = false,
}: AddPaymentMethodProps) => {
    //lo ideal sería useParams pero no coge la id
    let { url } = useRouteMatch();
    const pathParts = url.split("/");
    const id = pathParts[2];

    const createPaymentMethodUrl = buildUrl(isLead ? API.LEAD_SINGLE_PAYMENT_METHOD : API.CLIENT_SINGLE_PAYMENT_METHOD, { id });

    const notification = (state: "remove" | "create" | "update") => {
        switch (state) {
            case "create":
                return NOTIFY_CLIENT_PAYMENT_METHOD_CREATED.OK;
            case "update":
                return NOTIFY_CLIENT_PAYMENT_METHOD_UPDATED.OK;
            case "remove":
                return NOTIFY_CLIENT_PAYMENT_METHOD_DELETED.OK;
            default:
                return NOTIFY_CLIENT_PAYMENT_METHOD_CREATED.OK;
        }
    };

    const handleOnSuccess = async (state: "remove" | "create" | "update") => {
        onCancel();
        openNotification(notification(state));
        await mutate();
    };

    const [isCreating, createPaymentMethod] = useUpdate(
        createPaymentMethodUrl,
        () => handleOnSuccess(selectedPaymentMethodData ? "update" : "create"),
        useHandleError()
    );

    const removePaymentMethodUrl = useMemo(() => {
        if (selectedPaymentMethodData) {
            return buildUrl(API.DELETE_CLIENT_PAYMENT_METHOD, {
                id,
                paymentMethodId: selectedPaymentMethodData.id,
            });
        }
        return null;
    }, [id, selectedPaymentMethodData]);

    const [isRemoving, removePaymentMethod] = useDelete(
        removePaymentMethodUrl || "",
        () => handleOnSuccess("remove"),
        useHandleError()
    );

    const defaultPaymentFormValues = (selectedPaymentMethodData?: PaymentMethod) => {
        if (selectedPaymentMethodData) {
            return {
                id: selectedPaymentMethodData.id,
                paymentMethod: selectedPaymentMethod || PaymentMethodType.CreditCard,
                cardHolderName: selectedPaymentMethodData.creditCard?.cardHolderName || undefined,
                creditCardNumber:
                    selectedPaymentMethodData.creditCard?.creditCardNumber || undefined,
                expDate: selectedPaymentMethodData.creditCard?.expirationDate || undefined,
                ccv: selectedPaymentMethodData.creditCard?.ccv || undefined,
                bankName: selectedPaymentMethodData.bankAccount?.bankName || undefined,
                accountNumber: selectedPaymentMethodData.bankAccount?.accountNumber || undefined,
                routingNumber: selectedPaymentMethodData.bankAccount?.routingNumber || undefined,
                xpSystem: selectedPaymentMethodData.xpInformation?.xpSystem || undefined,
            };
        }

        return {
            id: crypto.randomUUID(),
            paymentMethod: selectedPaymentMethod || PaymentMethodType.CreditCard,
        };
    };

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
        setError,
        clearErrors,
    } = useForm<PaymentFormValues>({
        defaultValues: defaultPaymentFormValues(selectedPaymentMethodData),
        shouldUnregister: true,
    });

    const selectedMethod = watch("paymentMethod");

    const onSubmit = (data: PaymentFormValues) => {
        if (hasErrors(data)) return;
        createPaymentMethod(data);
    };

    const hasErrors = (data: PaymentFormValues): boolean => {
        if (selectedMethod !== PaymentMethodType.CreditCard) {
            return false;
        }

        const ccNumber = data.creditCardNumber || "";
        const cvc = data.ccv || "";

        const ccType = getCardTypeByValue(ccNumber);

        let hasError = false;

        if (data.paymentMethod !== PaymentMethodType.CreditCard) return hasError;

        if (!ccType || ccNumber.length !== ccType.maxCardNumberLength) {
            setError("creditCardNumber", { type: "invalidCard", message: "Card is invalid" });
            hasError = true;
        }

        if (cvc.length !== ccType?.cvcLength) {
            setError("ccv", { type: "invalidCvc", message: "Invalid CVC" });
            hasError = true;
        }

        return hasError;
    };

    const handleOnCancel = () => {
        onCancel();
    };

    useEffect(() => {
        reset({
            ...defaultPaymentFormValues(selectedPaymentMethodData),
            paymentMethod: selectedMethod,
        });
    }, [selectedMethod, selectedPaymentMethodData]);

    useEffect(() => {
        register("creditCardNumber");
        register("ccv");
        register("expDate", {
            required: selectedMethod === PaymentMethodType.CreditCard,
        });
    }, [register, selectedMethod]);

    const handleOnRemove = () => {
        if (!selectedPaymentMethodData || !removePaymentMethodUrl) return;

        removePaymentMethod(selectedPaymentMethodData);
    };

    return (
        <section className="w-full my-6 flex flex-col gap-4 bg-white">
            <h1 className="text-xl font-semibold">Add Payment Method</h1>
            <div className="w-full flex flex-col gap-1">
                {/* Payment Method */}
                <DropDownGeneric
                    title="Payment Method"
                    name="payment_method"
                    placeholder="Select Method"
                    className="flex-col"
                    titleClassname="text-r-medium-small-2"
                    inputClassName="mb-4"
                    menuPosition="fixed"
                    editing={!selectedPaymentMethodData}
                    readonly={false}
                    selectOptions={paymentMethodOptions}
                    isObject={false}
                    showBlankOption={false}
                    value={selectedMethod || ""}
                    onChangeFunction={(val) =>
                        setValue("paymentMethod", val, { shouldValidate: true })
                    }
                />
                {errors.paymentMethod && <ErrorText msg={FORM_MSG.REQUIRED} />}

                {/* CREDIT CARD FIELDS */}
                {selectedMethod === PaymentMethodType.CreditCard && (
                    <>
                        <InputRFH
                            name="cardHolderName"
                            title="Card Holder Name"
                            inputRegister={register}
                            validation={{
                                required: selectedMethod === PaymentMethodType.CreditCard,
                            }}
                            editing
                        >
                            {errors.cardHolderName && <ErrorText msg={FORM_MSG.REQUIRED} />}
                        </InputRFH>


                        <CCInput
                            title="Credit Card Number"
                            name="creditCardNumber"
                            editing
                            hidden={false}
                            value={watch("creditCardNumber") || ""}
                            setValue={setValue}
                            setError={setError}
                            clearErrors={clearErrors}
                            cvcValue={watch("ccv")}
                            checkCvc="ccv"
                        >
                            {errors.creditCardNumber && (
                                <ErrorText msg={errors.creditCardNumber.message || FORM_MSG.REQUIRED} />
                            )}
                        </CCInput>

                        <div className="flex w-full gap-3">
                            <ExpDate
                                className="w-full flex-col"
                                title="Exp Date"
                                editing
                                value={watch("expDate") || ""}
                                onChangeFunction={(val) => {
                                    setValue("expDate", val);
                                }}
                                cutYear
                            >
                                {errors.expDate && <ErrorText msg={FORM_MSG.REQUIRED} />}
                            </ExpDate>

                            <CVCInput
                                name="ccv"
                                title="CCV"
                                editing
                                hidden={false}
                                value={watch("ccv") || ""}
                                cardValue={watch("creditCardNumber")}
                                checkCard="creditCardNumber"
                                setValue={setValue}
                                setError={setError}
                                clearErrors={clearErrors}
                                className="w-full flex-col"
                            >
                                {errors.ccv && (
                                    <ErrorText msg={errors.ccv.message || FORM_MSG.REQUIRED} />
                                )}
                            </CVCInput>
                        </div>
                    </>
                )}

                {/* BANK ACCOUNT FIELDS */}
                {selectedMethod === PaymentMethodType.BankAccount && (
                    <>
                        <InputRFH
                            name="bankName"
                            title="Bank Name"
                            editing
                            inputRegister={register}
                            validation={{
                                required: selectedMethod === PaymentMethodType.BankAccount,
                            }}
                        >
                            {errors.bankName && <ErrorText msg={FORM_MSG.REQUIRED} />}
                        </InputRFH>
                        <InputRFH
                            name="accountNumber"
                            title="Account Number"
                            editing
                            inputRegister={register}
                            validation={{
                                required: selectedMethod === PaymentMethodType.BankAccount,
                            }}
                        >
                            {errors.accountNumber && <ErrorText msg={FORM_MSG.REQUIRED} />}
                        </InputRFH>
                        <InputRFH
                            name="routingNumber"
                            title="Routing Number"
                            editing
                            inputRegister={register}
                            validation={{
                                required: selectedMethod === PaymentMethodType.BankAccount,
                            }}
                        >
                            {errors.routingNumber && <ErrorText msg={FORM_MSG.REQUIRED} />}
                        </InputRFH>
                    </>
                )}

                {/* XP FIELDS */}
                {selectedMethod === PaymentMethodType.XPInformation && (
                    <InputRFH
                        name="xpSystem"
                        title="XP System"
                        editing
                        inputRegister={register}
                        validation={{
                            required: selectedMethod === PaymentMethodType.XPInformation,
                        }}
                    >
                        {errors.xpSystem && <ErrorText msg={FORM_MSG.REQUIRED} />}
                    </InputRFH>
                )}
                {errors.xpSystem && <ErrorText msg={FORM_MSG.REQUIRED} />}

                <div className="flex self-end gap-3 mt-3">
                    <button
                        type="button"
                        className="button-crm px-4 py-2 rounded-md text-white text-r-medium-small-2 min-w-[150px] flex justify-center"
                        disabled={isCreating}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {isCreating ? (
                            <Loader type="ThreeDots" color="var(--color-color2)" height={20} width={20} />
                        ) : (
                            <>
                                {selectedPaymentMethodData
                                    ? "Update Payment Method"
                                    : "Create Payment Method"}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="button-crm px-4 py-2 !bg-color11 rounded-md text-white text-r-medium-small-2"
                        onClick={
                            selectedPaymentMethodData
                                ? () => handleOnRemove()
                                : () => handleOnCancel()
                        }
                    >
                        {isRemoving ? (
                            <Loader type="ThreeDots" color="var(--color-color2)" height={20} width={20} />
                        ) : (
                            <>
                                {selectedPaymentMethodData ? "Remove" : "Cancel"}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </section>
    );
};
